import { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { products, categories, categoryGroups } from '@/mocks/products';
import type { Product, Category } from '@/mocks/products';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';
import ProductCard from '@/components/base/ProductCard';
import InquiryModal from '@/pages/home/components/InquiryModal';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const [inputValue, setInputValue] = useState(query);
  const [selectedCategory, setSelectedCategory] = useState<Category | ''>('');
  const [modalProduct, setModalProduct] = useState<Product | null>(null);

  const categoryZhMap: Record<string, string> = {
    '탕류': '汤类', '해조류': '海藻类', '음료·두유': '饮料·豆乳', '차류': '茶类',
    '스낵·김': '零食·海苔', '생활용품': '生活用品', '김치·반찬': '泡菜·小菜',
    '스킨케어·뷰티': '护肤·美妆', '섬유향수': '衣物香水', '핸드크림': '护手霜',
    '운동화': '运动鞋', '건강용품': '健康用品',
  };

  useEffect(() => {
    setInputValue(query);
  }, [query]);

  const results = useMemo(() => {
    return products.filter(p => {
      const matchQuery = query === '' || p.name.toLowerCase().includes(query.toLowerCase()) || p.category.includes(query);
      const matchCat = selectedCategory === '' || p.category === selectedCategory;
      return matchQuery && matchCat;
    });
  }, [query, selectedCategory]);

  const searchJsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'SearchResultsPage',
    name: query ? `"${query}" 검색 결과 | 한국 B2B 도매 카탈로그` : '상품 검색 | 한국 B2B 도매 카탈로그',
    url: `${siteUrl}/search${query ? `?q=${encodeURIComponent(query)}` : ''}`,
    description: '한국 B2B 도매 카탈로그 상품 검색 페이지. 식품, 화장품, 생활용품 등 2,000여 개 상품을 검색하세요.',
  }), [query]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim()) {
      setSearchParams({ q: inputValue.trim() });
    }
  };

  const handleClear = () => {
    setInputValue('');
    setSelectedCategory('');
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
      <SeoHead
        title={query ? `"${query}" 검색 결과 | 한국 B2B 도매 카탈로그` : '상품 검색 | 한국 B2B 도매 카탈로그'}
        description="한국 B2B 도매 카탈로그 상품 검색. 식품, 화장품, 생활용품 등 2,000여 개 도매 상품을 검색하세요."
        keywords="한국 상품 검색, B2B 도매 검색, 식품 도매 검색, 화장품 도매 검색"
        canonical={`${siteUrl}/search`}
        jsonLd={searchJsonLd}
      />
      <Header forceScrolled />
      <div style={{ height: 'calc(108px + var(--banner-height, 0px))' }} />

      {/* Search Header */}
      <section className="pb-6 sm:pb-8 px-4 md:px-8 lg:px-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-screen-xl mx-auto">
          <div className="pt-6 sm:pt-8" />
          <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-2 sm:mb-3">Search / 搜索</p>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-black text-gray-900 mb-1">商品搜索</h1>
          <p className="text-sm text-gray-500 mb-4 sm:mb-6">상품 검색</p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-2 max-w-2xl">
            <div className="flex-1 relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
                <i className="ri-search-line text-gray-400 text-base"></i>
              </div>
              <input
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                placeholder="搜索商品名称（例：参鸡汤、牛排骨汤…）/ 상품명 검색"
                className="w-full pl-11 pr-10 py-3 sm:py-3.5 border-2 border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-black transition-colors bg-white"
              />
              {inputValue && (
                <button type="button" onClick={handleClear} className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-black transition-colors cursor-pointer">
                  <i className="ri-close-line text-base"></i>
                </button>
              )}
            </div>
            <button
              type="submit"
              className="w-full sm:w-auto px-6 py-3 sm:py-3.5 bg-black text-white font-bold text-sm rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索 / 검색
            </button>
          </form>

          {/* Quick Keywords */}
          <div className="mt-3 sm:mt-4 flex flex-wrap gap-1.5 sm:gap-2">
            <span className="text-xs text-gray-400 self-center mr-1 whitespace-nowrap">推荐关键词 / 추천 키워드:</span>
            {['삼계탕', '소갈비탕', '도가니탕', '곱창전골', '소꼬리곰탕', '육개장', '왕갈비탕'].map(kw => (
              <button
                key={kw}
                onClick={() => { setInputValue(kw); setSearchParams({ q: kw }); }}
                className="text-xs px-2.5 sm:px-3 py-1 sm:py-1.5 border border-gray-300 text-gray-600 rounded-full hover:bg-black hover:text-white hover:border-black transition-all cursor-pointer whitespace-nowrap"
              >
                {kw}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Filter & Results */}
      <section className="px-4 md:px-8 lg:px-12 py-6 sm:py-8">
        <div className="max-w-screen-xl mx-auto">
          {/* Category Filter - scrollable on mobile */}
          <div className="mb-4 sm:mb-6">
            <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1 scrollbar-hide flex-nowrap sm:flex-wrap">
              <button
                onClick={() => setSelectedCategory('')}
                className={`px-3 sm:px-4 py-1.5 text-xs font-bold rounded-full transition-all whitespace-nowrap cursor-pointer shrink-0 ${
                  selectedCategory === '' ? 'bg-black text-white' : 'border border-gray-200 text-gray-600 hover:border-black hover:text-black'
                }`}
              >
                全部 / 전체
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(selectedCategory === cat ? '' : cat)}
                  className={`px-3 sm:px-4 py-1.5 text-xs font-bold rounded-full transition-all whitespace-nowrap cursor-pointer shrink-0 ${
                    selectedCategory === cat ? 'bg-black text-white' : 'border border-gray-200 text-gray-600 hover:border-black hover:text-black'
                  }`}
                >
                  {categoryZhMap[cat] || cat} <span className={`font-normal text-[10px] ${selectedCategory === cat ? 'opacity-70' : 'opacity-50'}`}>/ {cat}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Result Count */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 sm:mb-6">
            <p className="text-sm text-gray-600">
              {query ? (
                <><strong className="text-gray-900">&quot;{query}&quot;</strong> 搜索结果 <strong className="text-gray-900">{results.length}件</strong> <span className="text-gray-400 text-xs">/ 검색 결과 {results.length}개</span></>
              ) : (
                <>全部商品 <strong className="text-gray-900">{results.length}件</strong> <span className="text-gray-400 text-xs">/ 전체 상품 {results.length}개</span></>
              )}
              {selectedCategory && <span className="text-gray-500"> · {selectedCategory} 筛选中 / 필터 적용됨</span>}
            </p>
            {(query || selectedCategory) && (
              <button
                onClick={() => { setInputValue(''); setSelectedCategory(''); setSearchParams({}); }}
                className="text-xs text-gray-400 hover:text-black transition-colors cursor-pointer flex items-center gap-1 self-start sm:self-auto"
              >
                <i className="ri-refresh-line"></i> 重置 / 초기화
              </button>
            )}
          </div>

          {/* Results Grid */}
          {results.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 md:gap-5">
              {results.map((product, i) => (
                <div key={product.id} className="animate-fadeIn" style={{ animationDelay: `${i * 40}ms` }}>
                  <ProductCard product={product} onInquiry={setModalProduct} />
                </div>
              ))}
            </div>
          ) : (
            <div className="py-16 sm:py-24 text-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                <i className="ri-search-line text-2xl sm:text-3xl text-gray-400"></i>
              </div>
              <h3 className="text-base sm:text-lg font-black text-gray-900 mb-1">暂无搜索结果</h3>
              <p className="text-xs text-gray-400 mb-1">검색 결과가 없습니다</p>
              <p className="text-sm text-gray-500 mb-6 px-4">请尝试其他关键词或更改分类筛选。/ 다른 키워드로 검색하거나 카테고리 필터를 변경해 보세요.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center px-4">
                <button
                  onClick={handleClear}
                  className="px-6 py-3 border-2 border-black text-black font-bold text-sm rounded-xl hover:bg-black hover:text-white transition-all whitespace-nowrap cursor-pointer"
                >
                  查看全部商品 / 전체 상품 보기
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-3 bg-black text-white font-bold text-sm rounded-xl hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer"
                >
                  返回首页 / 홈으로 이동
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
      <InquiryModal product={modalProduct} onClose={() => setModalProduct(null)} />
    </div>
  );
}
